import { Map, List } from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';

import { createImmutableSelector } from './selector-utils';

import { calcTotalUnreadMessages, newestFirst } from 'flow/utils';
import {
    isDirectMessageSession,
    isNumberSMSChat,
    roomTypes,
} from 'flow/utils/message-utils-TW';
import { newestFirstSorter } from 'flow/utils/sorter-utils';
import { selectOwnChatUserKey } from './own-extension-selectors';
import {
    getExtensionsFilteredBySearchText,
    getSearchText,
    getExtensionKeyFromSession,
} from './selectors';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatMember, ChatSession, SortInfo } from 'flow/types/Chats';
import {
    ChatSessionKey,
    ChatUserKey,
    ExtensionKey,
} from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { selectOwnExtensionKey } from './ExtensionSelectors';

const selectSessionSortInfo = (state: State) =>
    state.chats.get('sessionSortInfo', Map());
const selectSessions = (state: State) => state.chats.get('sessions');

/** Whether there are any chat sessions at all. A boolean, so that a caller
 *  waiting for the first one to arrive does not subscribe to the map itself -
 *  that is rewritten whenever a message arrives in any chat. */
export const selectHasChatSessions = (state: State) =>
    !!state.chats.get('sessions')?.size;
export const selectPublicSessions = (state: State) =>
    state.chats.get('publicSessions', List());

/** The extension a chatUser belongs to. The chats reducer keeps this map of
 *  chatUserKey -> extensionKey from the extension list (RESULT_EXTENSIONS). */
export const selectExtensionKeyFromChatUserKey = (
    state: State,
    chatUserKey?: ChatUserKey,
): ExtensionKey | undefined => {
    if (!chatUserKey) return undefined;
    return state.chats?.getIn(['extensions', chatUserKey]);
};

export const selectMemberInChat = (
    state: State,
    chatSessionId?: number | string,
) => {
    if (!chatSessionId) return false;
    return !!state.chats.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
};

export const selectChatSessionIsDirectMessage = (
    state: State,
    chatSessionId?: number | string,
) => {
    if (!chatSessionId) return false;
    const session = state.chats?.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
    return isDirectMessageSession(session);
};

/** One chat session, by its composed key. A row that selects its own session
 *  this way re-renders when that chat changes and not when any other does -
 *  unlike reading it out of the whole sessions map, which is rewritten
 *  whenever a message arrives in any chat at all. */
export const selectChatSession = (
    state: State,
    chatSessionKey?: ChatSessionKey,
): TypedMap<ChatSession> | undefined => {
    if (!chatSessionKey) return undefined;
    return state.chats?.getIn(['sessions', chatSessionKey]);
};

export const selectChatSessionIsRoom = (
    state: State,
    chatSessionId?: number | string,
) => {
    if (!chatSessionId) return false;
    const session = state.chats?.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
    return session?.get('roomType') === roomTypes.room;
};

export const selectChatSessionOwnerExtensionKey = (
    state: State,
    chatSessionId?: number | string,
): ExtensionKey | undefined => {
    if (!chatSessionId) return undefined;
    const session = state.chats?.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
    return session?.getIn(['owner', 'extensionKey']);
};

export const selectChatName = (
    state: State,
    chatSessionId?: number | string,
) => {
    if (!chatSessionId) return undefined;
    return state.chats.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
        'name',
    ]);
};

export const selectChatClientKey = (state: State) =>
    state.chats.getIn(['client', 'key'], null);

/**
 * Takes the composed ChatSessionKey directly (callers that already hold one,
 * e.g. meeting-selectors, rather than a raw numeric id) — unlike the
 * selectors above, which build the key themselves from a raw id.
 */
export const selectChatSessionAdmins = (
    state: State,
    chatSessionKey?: ChatSessionKey,
): List<ChatUserKey> => {
    if (!chatSessionKey) return List();
    return state.chats?.getIn(['sessions', chatSessionKey, 'admins'], List());
};

/** The session's members, or an empty list while it has not loaded. Takes the
 *  composed key, as selectChatSessionAdmins above does. */
export const selectChatSessionMembers = (
    state: State,
    chatSessionKey?: ChatSessionKey,
): List<TypedMap<ChatMember>> => {
    if (!chatSessionKey) return List();
    return state.chats?.getIn(['sessions', chatSessionKey, 'members'], List());
};

export const selectChatSessionRoomType = (
    state: State,
    chatSessionKey?: ChatSessionKey,
): string | undefined => {
    if (!chatSessionKey) return undefined;
    return state.chats?.getIn(['sessions', chatSessionKey, 'roomType']);
};

/** Whether the session is in the store at all - a boolean, so that a caller
 *  waiting for it to arrive does not subscribe to the session itself, which is
 *  rewritten on every message and every read receipt. */
export const selectChatSessionIsLoaded = (
    state: State,
    chatSessionKey?: ChatSessionKey,
): boolean =>
    !!chatSessionKey && !!state.chats?.hasIn(['sessions', chatSessionKey]);

/**
 * The instant a session was last active, for ordering the chat list.
 *
 * The shared `newestFirstSorter` reads both its timestamps with moment inside
 * the comparison, so ordering N sessions parses about 2*N*log N of them, and
 * the chat list is re-sorted on every arriving message. Immutable computes a
 * `sortBy` key once per entry, so reading the instant here costs N instead.
 * Kept local to the chat list rather than changed in `sorter-utils`, which
 * call records, tickets and channels also sort through.
 *
 * A session with nothing to sort on has no instant, and goes last. An
 * unreadable timestamp goes last too, rather than comparing equal to every
 * other one as it did before.
 */
const readActivityTime = (latestActivity?: string) => {
    if (!latestActivity) return null;
    const time = moment(latestActivity).valueOf();
    return Number.isNaN(time) ? null : time;
};

/** Newest first, sessions with no activity last, as `newestFirstSorter`. */
const newestActivityFirst = (time1: number | null, time2: number | null) => {
    if (time1 === null && time2 === null) return 0;
    if (time1 === null) return 1;
    if (time2 === null) return -1;
    return time2 - time1;
};

/**
 * This selector filters a list of chat sessions after a searchText, and orders it
 * based on the latest activity in each session. (Sessions with newer activities comes
 * before sessions with older activity.) Archived sessions are hidden if they not match
 * the search string.
 *
 * It places chats with no activity at the end of the list.
 *
 * If no search text is entered, every session's key is returned.
 */
const sortAndFilterChatSessionKeys = createSelector(
    [getSearchText, selectSessionSortInfo],
    (searchText, sessions) => {
        const lowerCaseSearch = searchText ? searchText.toLowerCase() : '';
        // All strings include the empty string. The empty string also includes the empty string.
        const matchesSearch = (session: TypedMap<SortInfo>) =>
            session
                .get('description', '')
                .toLowerCase()
                .includes(lowerCaseSearch);
        const activity = (session: TypedMap<SortInfo>) =>
            readActivityTime(session.get('latestActivity'));

        const sorted =
            lowerCaseSearch === ''
                ? sessions
                      .filter(matchesSearch)
                      .filter((session) => !session.get('archived'))
                      .sortBy(activity, newestActivityFirst)
                : sessions
                      .filter(matchesSearch)
                      .sortBy(activity, newestActivityFirst);

        return List(sorted.keySeq());
    },
);

/* The sort info is rewritten by every arriving message, but the order it
 * produces usually is not, so the ids are handed out as the same List. */
export const selectSortedAndFilteredChatSessionKeys = createImmutableSelector(
    [sortAndFilterChatSessionKeys],
    (chatSessionKeys) => chatSessionKeys,
);

export const selectSortedArchivedChatSessions = createSelector(
    [selectSessions],
    (sessions) =>
        sessions
            .filter((session) => !!session.get('chatArchived'))
            .sortBy(
                (chatSession) => chatSession.getIn(['latestMessage', 'sent']),
                newestFirstSorter,
            ),
);

/**
 * The colleagues this user has no chat with yet, as extension keys. Every
 * colleague is measured against every session, so the work grows with both.
 */
const findColleaguesWithoutChat = createSelector(
    [
        getExtensionsFilteredBySearchText,
        selectSessionSortInfo,
        selectOwnExtensionKey,
    ],
    (
        extensions: TypedMap<Record<ExtensionKey, TypedMap<Extension>>>,
        sessions,
        ownExtensionKey,
    ) => {
        const sessionExtensionKeys = sessions
            .map((s) => getExtensionKeyFromSession(s, ownExtensionKey))
            .filter((extensionKey) => !!extensionKey)
            .valueSeq();

        const colleaguesWithoutChats: ExtensionKey[] = [];

        extensions.forEach((extension) => {
            const sessionExists = sessionExtensionKeys.some((extensionKey) =>
                extensionKey.includes(extension.get('key')),
            );
            if (!sessionExists) {
                colleaguesWithoutChats.push(extension.get('key'));
            }
        });

        return List(colleaguesWithoutChats);
    },
);

/* Recomputed from the same sort info, and just as rarely a different list. */
const colleaguesWithoutChat = createImmutableSelector(
    [findColleaguesWithoutChat],
    (extensionKeys) => extensionKeys,
);

/* The check stands in front of the chain rather than inside it: a selector
 * computes its inputs before its result function runs, and with no search text
 * getExtensionsFilteredBySearchText hands over every colleague there is. */
export const selectColleaguesWithoutChat = (state: State) =>
    getSearchText(state) ? colleaguesWithoutChat(state) : List<ExtensionKey>();

const findHasNoSmsChatSession = createSelector(
    [getSearchText, selectSessionSortInfo],
    (searchText, sessions) => {
        return (
            sessions.filter((session) => {
                if (
                    !isNumberSMSChat(null, session) ||
                    session.get('roomType') !== 'session' ||
                    session.get('members').size !== 2
                ) {
                    return false;
                }

                return session
                    .get('members')
                    .some(
                        (member) => member.get('mobileNumber') === searchText,
                    );
            })?.size === 0
        );
    },
);

/* Only consulted while searching - see the SMS row - so there is nothing to
 * look for until there is something to match against. */
export const selectHasNoSmsChatSessionWithFilter = (state: State) =>
    getSearchText(state) ? findHasNoSmsChatSession(state) : true;

export const selectJoinedPublicSessions = createSelector(
    [selectPublicSessions, selectOwnChatUserKey],
    (chatSessions, chatUserKey) =>
        chatSessions
            .filter(
                (o) =>
                    !!o
                        .get('members')
                        .find((member) => member.get('key') === chatUserKey),
            )
            .sort((a, b) => newestFirst(a, b)),
);

export const selectAvailablePublicSessions = createSelector(
    [selectPublicSessions, selectOwnChatUserKey],
    (chatSessions, chatUserKey) =>
        chatSessions
            .filter(
                (o) =>
                    !o
                        .get('members')
                        .find((member) => member.get('key') === chatUserKey),
            )
            .sort((a, b) => newestFirst(a, b)),
);

export const selectChatSessionByChatUser = (
    state: State,
    chatUserKey: ChatUserKey,
    extensionKey: ExtensionKey,
) => {
    if (!chatUserKey || !extensionKey) {
        return null;
    }
    return state.chats.get('sessions').findKey((session) => {
        const members = session.get('members');
        if (
            members.size === 2 &&
            !session.get('isChatRoom') &&
            session.get('roomType') === 'session'
        ) {
            return (
                members.some((member) => member.get('key') === chatUserKey) &&
                members.some(
                    (member) => member.get('extensionKey') === extensionKey,
                )
            );
        }
        return false;
    });
};

export const selectNumberUnreadChatMessages = createSelector(
    [selectSessions],
    (sessions) => calcTotalUnreadMessages(sessions),
);

/** First name of the chat member on the given extension, or undefined when the
 *  session has no such member. Reads the members list rather than the session,
 *  so it holds its value while the very dynamic fields alongside it -
 *  latestMessage, lastReadMessage, numberOfUnreadMessages - churn on every
 *  message. A component that subscribes to the session instead re-renders on
 *  all of them. */
export const selectChatMemberFirstName = (
    state: State,
    chatSessionKey: ChatSessionKey,
    // Loosely typed on purpose: callers read this off a message with getIn,
    // which does not narrow to ExtensionKey.
    extensionKey: string,
): string | undefined => {
    // A member's extensionKey is optional - a number chat has members without
    // one - so matching on an absent key would find the first of those and
    // report a name belonging to somebody else entirely.
    if (!extensionKey) return undefined;

    return state.chats
        .getIn(['sessions', chatSessionKey, 'members'], List())
        .find((member) => member.get('extensionKey') === extensionKey)
        ?.getIn(['contact', 'firstName']);
};
